import { useState, useEffect, useCallback } from 'react';
import { NetlifyLogsService } from '~/lib/services/NetlifyLogsService';
import { selectionStore } from '~/lib/persistence/selectionStore';
import type { LogMessage, UseNetlifyLogsReturn } from '~/lib/types/netlify';
import { netlifyStore } from '~/lib/stores/netlify';

// Singleton instance to be shared across components
let logsServiceInstance: NetlifyLogsService | null = null;
// eslint-disable-next-line prefer-const
let logsListeners: Set<(logs: LogMessage[]) => void> = new Set();
let globalLogs: LogMessage[] = [];

// Track connecting attempts by deployment ID
const connectingDeployments = new Set<string>();

export function useNetlifyLogs(
  deployedId: string | null,
  accessToken: string | null | undefined,
): UseNetlifyLogsReturn {
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [isViewingLogs, setIsViewingLogs] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Update local state when global logs change
  useEffect(() => {
    const updateLogs = (newLogs: LogMessage[]) => {
      setLogs([...newLogs]);
    };

    // Register this component as a listener
    logsListeners.add(updateLogs);

    // Initialize with current logs
    if (globalLogs.length > 0) {
      setLogs([...globalLogs]);
    }

    return () => {
      // Clean up listener when component unmounts
      logsListeners.delete(updateLogs);
    };
  }, []);

  // Cleanup function that can be called explicitly
  const cleanupLogs = useCallback(() => {
    console.log('[useNetlifyLogs] Cleaning up logs');

    if (logsServiceInstance) {
      logsServiceInstance.disconnect();
      logsServiceInstance = null;

      // Always clear logs on cleanup - removed preserveLogs parameter
      globalLogs = [];
      console.log('[useNetlifyLogs] Logs have been cleared');

      // Notify all listeners about the empty logs
      logsListeners.forEach((listener) => listener(globalLogs));
    }
  }, []);

  // Handle deployedId or accessToken changes
  useEffect(() => {
    /*
     * If we have an active connection for a different deployment or token,
     * clean it up when deployedId or accessToken changes
     */
    if (
      logsServiceInstance &&
      deployedId &&
      logsServiceInstance.isConnected() &&
      !logsServiceInstance.isConnected(deployedId)
    ) {
      console.log('Deployment ID changed, cleaning up previous logs connection');
      cleanupLogs();
    }
  }, [deployedId, accessToken, cleanupLogs]);

  // Connect to logs
  const connectToLogs = useCallback(() => {
    console.log('[useNetlifyLogs] Connecting to logs', {
      accessToken: accessToken ? 'Present' : 'Missing',
      deployedId,
      isConnecting,
      isViewingLogs,
      existingLogs: globalLogs.length,
    });

    if (!accessToken || !deployedId) {
      console.warn('[useNetlifyLogs] Cannot connect - missing', !accessToken ? 'accessToken' : 'deployedId');
      return;
    }

    // If we already have a lot of logs, just set viewing state
    if (globalLogs.length > 5) {
      console.log('[useNetlifyLogs] Already have sufficient logs, skipping new connection');
      setIsViewingLogs(true);
      setIsConnecting(false);
      return;
    }

    // Check global connecting flag first - this prevents race conditions
    if (connectingDeployments.has(deployedId)) {
      console.log('[useNetlifyLogs] Global connection already in progress, skipping duplicate call');
      setIsViewingLogs(true);
      return;
    }

    // If we already have a connection for the SAME deployment, just use it
    if (logsServiceInstance?.isConnected(deployedId)) {
      console.log('[useNetlifyLogs] Already connected to logs for this deployment - reusing connection');
      setIsViewingLogs(true);
      return;
    }

    // Set global connecting flag
    connectingDeployments.add(deployedId);
    setIsConnecting(true);
    setIsViewingLogs(true);

    const siteId = selectionStore.get().netlifySiteId;

    console.log('[useNetlifyLogs] Site ID from selectionStore:', siteId);

    if (!siteId) {
      console.error('[useNetlifyLogs] Site ID not found. Cannot connect to logs.');

      const errorLog: LogMessage = {
        message: 'Error: Site ID not found. Cannot connect to logs.',
        timestamp: new Date().toISOString(),
        level: 'error',
        section: 'logs',
        type: 'error',
      };

      globalLogs = [errorLog];

      /*
       * logsListeners.forEach because:
       * a. Multiple components need to know about new logs
       * b. Each component needs to update its local state
       * c. Ensures real-time updates across all components showing logs
       */
      logsListeners.forEach((listener) => listener(globalLogs));
      setIsConnecting(false);
      connectingDeployments.delete(deployedId); // Clear global flag
      return;
    }

    // Check if deployment has error status
    const currentStatus = netlifyStore.get().deploymentStatus;

    if (currentStatus === 'error') {
      console.log('[useNetlifyLogs] Connecting to logs for deployment with error status - may preserve existing logs');
    }

    // Create logs service if it doesn't exist or is for a different deployment
    console.log(`[useNetlifyLogs] Creating new logs service for deployId: ${deployedId}`);
    logsServiceInstance = new NetlifyLogsService({
      siteId,
      deployId: deployedId,
      accessToken,
      onMessage: (data) => {
        try {
          const logMessage: LogMessage = {
            message: data.message || JSON.stringify(data),
            timestamp: data.ts || new Date().toISOString(),
            level: data.level || 'info',
            section: data.section || 'logs',
            type: data.type || 'info',
          };

          globalLogs = [...globalLogs, logMessage];
          logsListeners.forEach((listener) => listener(globalLogs));
          setIsConnecting(false);
          connectingDeployments.delete(deployedId); // Clear global flag
        } catch (error) {
          console.error('[useNetlifyLogs] Error parsing log message:', error, 'Raw data:', data);
          connectingDeployments.delete(deployedId); // Clear global flag on error
        }
      },
      onError: (error) => {
        console.error('[useNetlifyLogs] Logs WebSocket error:', error);

        const errorLog: LogMessage = {
          message: `Error connecting to logs: ${error.toString()}`,
          timestamp: new Date().toISOString(),
          level: 'error',
          section: 'logs',
          type: 'error',
        };

        globalLogs = [...globalLogs, errorLog];
        logsListeners.forEach((listener) => listener(globalLogs));
        setIsConnecting(false);
        connectingDeployments.delete(deployedId); // Clear global flag
      },
      onClose: () => {
        console.log('[useNetlifyLogs] Logs connection closed');

        const closeLog: LogMessage = {
          message: 'Logs connection closed',
          timestamp: new Date().toISOString(),
          level: 'info',
          section: 'logs',
          type: 'info',
        };

        globalLogs = [...globalLogs, closeLog];
        logsListeners.forEach((listener) => listener(globalLogs));
        setIsConnecting(false);
        connectingDeployments.delete(deployedId); // Clear global flag
      },
      onOpen: () => {
        console.log('[useNetlifyLogs] Logs connection opened successfully');

        const openLog: LogMessage = {
          message: 'Connected to Netlify logs',
          timestamp: new Date().toISOString(),
          level: 'info',
          section: 'logs',
          type: 'info',
        };

        globalLogs = [...globalLogs, openLog];
        logsListeners.forEach((listener) => listener(globalLogs));
      },
    });

    // Connect to logs
    logsServiceInstance.connect();
  }, [accessToken, deployedId]);

  // Check if we should connect to logs - utility to prevent multiple connections
  const shouldConnectToLogs = useCallback((): boolean => {
    // Basic requirement checks
    if (!accessToken || !deployedId) {
      console.warn('[useNetlifyLogs] Cannot connect - missing', !accessToken ? 'accessToken' : 'deployedId');
      return false;
    }

    console.log('[useNetlifyLogs] Should connect to logs', {
      accessToken,
      deployedId,
      globalLogsLength: globalLogs.length,
      connectingDeployments: Array.from(connectingDeployments),
    });

    // If we already have a lot of logs, skip connecting
    if (globalLogs.length > 5) {
      console.log('[useNetlifyLogs] Already have sufficient logs, no need to connect');
      setIsViewingLogs(true);
      setIsConnecting(false);
      return false;
    }

    // Check global connecting flag - prevents race conditions
    if (connectingDeployments.has(deployedId)) {
      console.log('[useNetlifyLogs] Global connection already in progress');
      setIsViewingLogs(true);
      return false;
    }

    // If we already have a connection for the SAME deployment, use it
    if (logsServiceInstance?.isConnected(deployedId)) {
      console.log('[useNetlifyLogs] Already connected to this deployment');
      setIsViewingLogs(true);
      return false;
    }

    // All checks passed, safe to connect
    return true;
  }, [accessToken, deployedId, setIsViewingLogs]);

  // Safe connection method that first checks if we should connect
  const safeConnectToLogs = useCallback(() => {
    if (shouldConnectToLogs()) {
      connectToLogs();
      return true;
    }

    return false;
  }, [shouldConnectToLogs, connectToLogs]);

  // disconnectFromLogs is called when user clicks to hide logs UI
  const disconnectFromLogs = useCallback(() => {
    setIsViewingLogs(false);

    /*
     * We don't disconnect here because:
     * 1. Other components might still be viewing logs
     * 2. Keeps WebSocket connection alive for efficiency
     * 3. Prevents unnecessary reconnections
     */
  }, []);

  // Format timestamp (utility function)
  const formatTimestamp = useCallback((timestamp: string) => {
    try {
      // Handle numeric timestamp (milliseconds since epoch)
      if (!isNaN(Number(timestamp))) {
        const date = new Date(Number(timestamp));
        return date.toLocaleTimeString();
      }

      // Handle ISO string timestamp
      const date = new Date(timestamp);
      return date.toLocaleTimeString();
    } catch (error) {
      return timestamp;
    }
  }, []);
  const formatLogMessage = useCallback((message: string): string => {
    // For example: [2mmise[0m [34mruby[0m@3.4.3
    if (!message) {
      return '';
    }

    // Define color mapping for ANSI codes to CSS colors
    const colorMap: Record<string, string> = {
      '30': '#000000', // black
      '31': '#ff0000', // red
      '32': '#00ff00', // green
      '33': '#ffff00', // yellow
      '34': '#0000ff', // blue
      '35': '#ff00ff', // magenta
      '36': '#00ffff', // cyan
      '37': '#ffffff', // white
      '90': '#808080', // gray
      '91': '#ff6666', // light red
      '92': '#66ff66', // light green
      '93': '#ffff66', // light yellow
      '94': '#6666ff', // light blue
      '95': '#ff66ff', // light magenta
      '96': '#66ffff', // light cyan
      '97': '#ffffff', // white
    };

    // Replace ANSI codes with HTML spans using only inline styles
    let result = message;
    let currentStyles: Record<string, string> = {};

    // Replace ANSI color codes with HTML formatting
    result = result.replace(/\x1b\[(\d+)(?:;\d+)*m|\x1b\[0m/g, (match, code) => {
      // Reset all formatting
      if (match === '\x1b[0m' || !code) {
        currentStyles = {};
        return '</span>';
      } // Apply color or style based on code

      // Apply style based on code
      if (code) {
        const codeNum = parseInt(code);

        // Text colors (30-37, 90-97)
        if ((codeNum >= 30 && codeNum <= 37) || (codeNum >= 90 && codeNum <= 97)) {
          currentStyles.color = colorMap[code];
        }
        // Text formatting
        else if (code === '1') {
          currentStyles.fontWeight = 'bold';
        } else if (code === '2') {
          currentStyles.opacity = '0.8';
        } else if (code === '3') {
          currentStyles.fontStyle = 'italic';
        } else if (code === '4') {
          currentStyles.textDecoration = 'underline';
        }

        // Convert style object to CSS string
        const styleString = Object.entries(currentStyles)
          .map(([prop, value]) => `${prop}:${value}`)
          .join(';');

        return `<span style="${styleString}">`;
      }

      return '';
    });

    // Handle unclosed spans
    const openSpans = (result.match(/<span/g) || []).length;
    const closeSpans = (result.match(/<\/span>/g) || []).length;

    if (openSpans > closeSpans) {
      result += '</span>'.repeat(openSpans - closeSpans);
    }

    return result;
  }, []);

  // Get logs for error fixing
  const getLogsForErrorFix = useCallback((): string[] => {
    return globalLogs.map((log) => log.message);
  }, []);

  // Clean up the singleton when all instances(components) are unmounted
  useEffect(() => {
    console.log('[useNetlifyLogs] Cleanup effect');

    return () => {
      // If this is the last component using the logs, disconnect
      if (logsListeners.size === 0 && logsServiceInstance) {
        console.log('[useNetlifyLogs] Disconnecting from logs');
        cleanupLogs();
      }
    };
  }, [cleanupLogs]);

  return {
    logs,
    isConnecting,
    isViewingLogs,
    connectToLogs,
    disconnectFromLogs,
    formatTimestamp,
    formatLogMessage,
    getLogsForErrorFix,
    setIsViewingLogs,
    cleanupLogs,
    safeConnectToLogs,
  };
}
